import { useEffect, useState } from 'react';
import { ArrowLeft, Bell, Clock, Hourglass, Plus, RefreshCw, Smartphone, Ticket, Trophy } from 'lucide-react';
import type { PartySummary } from '@gmp/shared-types/browser';
import { Stage } from '@gmp/shared-ui';
import { formatLeft } from '@gmp/async-kit/ui';
import { navigate } from '../app/router';
import { sound } from '../app/prefs';
import { gameInfo } from '../app/catalog';
import { PHASE_LABEL } from '../app/games';
import { usePartyState } from '../App';
import { party } from '../net/client';
import { PUBLIC_APP_URL } from '../net/config';
import { deviceLink } from '../net/identity';
import { QR } from '../app/QR';
import { TurnAlerts } from '../app/TurnAlerts';

type Status = 'your-turn' | 'waiting' | 'results' | 'lobby';

function statusOf(p: PartySummary): Status {
  if (p.stage === 'FINAL_RESULTS') return 'results';
  if (p.stage === 'LOBBY') return 'lobby';
  return p.yourTurn ? 'your-turn' : 'waiting';
}

const STATUS: Record<Status, { label: string; className: string; icon: typeof Bell }> = {
  'your-turn': { label: 'Your turn', className: 'bg-[var(--color-brand)] text-ink', icon: Bell },
  waiting: { label: 'Waiting on others', className: '', icon: Hourglass },
  results: { label: 'Results ready', className: 'bg-good text-ink', icon: Trophy },
  lobby: { label: 'In the lobby', className: '', icon: Clock },
};

function PartyRow({ p, now }: { p: PartySummary; now: number }) {
  const g = gameInfo(p.gameId);
  const s = STATUS[statusOf(p)];
  const Icon = s.icon;
  const turn = g.asyncInfo?.turnSeconds;
  return (
    <button
      className="card p-4 text-left flex flex-col gap-2 border-2 hover:border-[var(--accent)]"
      onClick={() => { sound.play('select'); party.openParty(p.code); navigate({ name: 'party' }); }}
      data-testid={`party-${p.code}`}
    >
      <span className="flex items-start justify-between gap-2">
        <span className="min-w-0">
          <span className="block font-display font-black text-xl leading-tight truncate">{g.name}</span>
          <span className="block text-xs text-mute">{p.isHost ? 'You host' : `Hosted by ${p.host}`} · {p.players} players · {p.code}</span>
        </span>
        <span className={`chip shrink-0 text-xs ${s.className}`} data-testid={`party-status-${p.code}`}><Icon size={12} aria-hidden /> {s.label}</span>
      </span>
      {p.stage === 'IN_GAME' && (
        <span className="text-sm text-paper/85 flex flex-wrap gap-x-3 gap-y-1">
          <span>{p.round > 0 ? `Round ${p.round}/${p.totalRounds} · ` : ''}{PHASE_LABEL[p.phase ?? ''] ?? p.phase}</span>
          {p.endsAt !== null && <span className="text-mute">{formatLeft(p.endsAt - now)} left</span>}
          {p.yourTurn && turn && <span className="text-mute">about {turn[0] < 60 ? `${turn[0]}s` : `${Math.round(turn[0] / 60)} min`}–{turn[1] < 60 ? `${turn[1]}s` : `${Math.round(turn[1] / 60)} min`}</span>}
        </span>
      )}
    </button>
  );
}

/** Your Turn inbox: every open party you're in, your-turn first, one tap to jump back in. */
export function Parties() {
  const { parties, status } = usePartyState();
  const [now, setNow] = useState(() => party.now());
  useEffect(() => {
    // Opening the inbox from inside a party steps out of it (seat kept).
    if (party.getState().room) party.stepOut();
    if (status === 'online') party.myParties();
    const refresh = window.setInterval(() => status === 'online' && party.myParties(), 30_000);
    const clock = window.setInterval(() => setNow(party.now()), 1000);
    return () => { window.clearInterval(refresh); window.clearInterval(clock); };
  }, [status]);
  // A tapped turn alert opens /parties?open=CODE: jump straight into that party once it's listed.
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('open')?.toUpperCase();
    if (!code || !parties?.some((p) => p.code === code)) return;
    window.history.replaceState(null, '', '/parties');
    party.openParty(code);
    navigate({ name: 'party' });
  }, [parties]);
  const list = parties ?? [];
  const yours = list.filter((p) => statusOf(p) === 'your-turn').length;
  return (
    <Stage>
      <main className="flex-1 flex flex-col gap-4 safe-pad safe-top safe-bottom py-6 max-w-lg w-full mx-auto">
        <header className="flex items-center gap-3">
          <button className="btn btn-sm btn-secondary" onClick={() => navigate({ name: 'home' })} aria-label="Back"><ArrowLeft size={18} /></button>
          <h1 className="font-display font-black text-3xl flex-1">Your parties</h1>
          <button className="btn btn-sm btn-secondary" onClick={() => party.myParties()} aria-label="Refresh"><RefreshCw size={18} /></button>
        </header>
        <p className="text-mute" aria-live="polite" data-testid="parties-summary">
          {parties === null ? 'Checking your parties…' : list.length === 0 ? 'No open parties yet.' : yours ? `It's your turn in ${yours} ${yours === 1 ? 'party' : 'parties'}.` : 'Nothing waiting on you right now.'}
        </p>
        <section className="flex flex-col gap-3" aria-label="Open parties">
          {list.map((p) => <PartyRow key={p.code} p={p} now={now} />)}
        </section>
        {parties !== null && list.length === 0 && (
          <div className="grid grid-cols-2 gap-3">
            <button className="btn" onClick={() => navigate({ name: 'create' })}><Plus size={18} /> Create</button>
            <button className="btn btn-gold" onClick={() => navigate({ name: 'join' })}><Ticket size={18} /> Join</button>
          </div>
        )}
        <TurnAlerts />
        <AnotherDevice />
      </main>
    </Stage>
  );
}

function AnotherDevice() {
  const [open, setOpen] = useState(false);
  const { signedIn } = usePartyState();
  const link = deviceLink(PUBLIC_APP_URL);
  if (signedIn) {
    return (
      <section className="card p-4 flex items-center gap-2 text-sm text-mute">
        <Smartphone size={18} aria-hidden /> Signed in: sign in on your other device to see these parties there too.
      </section>
    );
  }
  if (!link) return null;
  return (
    <section className="card p-4 flex flex-col gap-3">
      <button className="flex items-center gap-2 font-display font-black text-lg text-left" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="another-device">
        <Smartphone size={18} aria-hidden /> Continue on another device
      </button>
      {open && (
        <>
          <p className="text-sm text-mute">Scan this on your other phone or computer to carry on as you, with all your parties. Anyone with this code can play as you, so only use it on your own devices.</p>
          <div className="self-center"><QR value={link} size={200} /></div>
          <button className="btn btn-sm btn-secondary" onClick={() => void navigator.clipboard?.writeText(link).then(() => party.notify('Link copied', 'good'))}>Copy the link instead</button>
        </>
      )}
    </section>
  );
}
